import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { isProfane } from '../../utils/profanity-filter';
import FirebaseTimeRangeSelector from '../../components/analytics/FirebaseTimeRangeSelector';
import './LeaderboardHealth.css';

const LeaderboardHealth = ({ analytics }) => {
  const { topPlayers, recentGames, totalGames } = analytics;
  const [filter, setFilter] = useState('all');

  // Check for profane names
  const profaneGames = recentGames.filter(game => isProfane(game.playerName));
  const profaneCount = profaneGames.length;

  // Filter recent games
  const filteredGames = recentGames.filter(game => {
    if (filter === 'profane') return isProfane(game.playerName);
    if (filter === 'clean') return !isProfane(game.playerName);
    return true;
  });

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(2);
    return `${mins}:${secs.toString().padStart(5, '0')}`;
  };

  const formatDate = (timestamp) => {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="leaderboard-health">
      <div className="health-overview">
        <div className="health-header">
          <h3>Leaderboard Health</h3>
          <FirebaseTimeRangeSelector />
        </div>
        <div className="health-stats">
          <div className="health-stat">
            <div className="stat-value">{totalGames}</div>
            <div className="stat-label">Total Submissions</div>
          </div>
          <div className="health-stat">
            <div className={`stat-value ${profaneCount > 0 ? 'warning' : 'success'}`}>
              {profaneCount}
            </div>
            <div className="stat-label">Profane Names Detected</div>
          </div>
          <div className="health-stat">
            <div className="stat-value">
              {((recentGames.length / totalGames) * 100).toFixed(1)}%
            </div>
            <div className="stat-label">Recent Activity</div>
          </div>
        </div>
      </div>

      <div className="top-players-section">
        <h4>Top 10 Players (Best Times)</h4>
        <table className="players-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Player</th>
              <th>Time</th>
              <th>Moves</th>
              <th>Mode</th>
              <th>Difficulty</th>
            </tr>
          </thead>
          <tbody>
            {topPlayers.map((player, index) => (
              <tr key={index} className={isProfane(player.playerName) ? 'profane-row' : ''}>
                <td className="rank">#{index + 1}</td>
                <td className="player-name">
                  {player.playerName}
                  {isProfane(player.playerName) && (
                    <span className="profane-badge">
                      <AlertTriangle size={14} />
                    </span>
                  )}
                </td>
                <td>{formatTime(player.time)}</td>
                <td>{player.moves}</td>
                <td>{player.mode}</td>
                <td>{player.difficulty || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="recent-games-section">
        <div className="section-header">
          <h4>Recent Games</h4>
          <div className="filter-buttons">
            <button
              className={filter === 'all' ? 'active' : ''}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={filter === 'clean' ? 'active' : ''}
              onClick={() => setFilter('clean')}
            >
              Clean
            </button>
            <button
              className={filter === 'profane' ? 'active' : ''}
              onClick={() => setFilter('profane')}
            >
              Profane ({profaneCount})
            </button>
          </div>
        </div>

        <table className="recent-games-table">
          <thead>
            <tr>
              <th>Player</th>
              <th>Time</th>
              <th>Moves</th>
              <th>Mode</th>
              <th>Difficulty</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {filteredGames.slice(0, 50).map((game, index) => (
              <tr key={index} className={isProfane(game.playerName) ? 'profane-row' : ''}>
                <td className="player-name">
                  {game.playerName}
                  {isProfane(game.playerName) && (
                    <span className="profane-badge">
                      <AlertTriangle size={14} />
                    </span>
                  )}
                </td>
                <td>{formatTime(game.time)}</td>
                <td>{game.moves}</td>
                <td>{game.mode}</td>
                <td>{game.difficulty || '-'}</td>
                <td className="timestamp">{formatDate(game.timestamp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeaderboardHealth;
